import React, { ComponentProps, useCallback } from 'react';
import { String, Ui } from '@silvertours/front-shared';
import styled from '@emotion/styled';
import { Runtime } from '@silvertours/front-entities';
import { format } from 'date-fns/format';
import { useTranslations } from 'next-intl';
import ArrowBack from '@engineering/icons/arrow-back';
import ArrowForward from '@engineering/icons/arrow-forward';
import Edit from '@engineering/icons/edit';
import Info from '@engineering/icons/info';
import { UiLegacy } from '@silvertours/front-legacy-shared';
import { DEFAULT_AGE, useSearchContext } from '../SearchFormLegacy/context';
import { SceneProps, WizardOverlay } from './common';
import { CalendarScene } from './CalendarScene';
import { SelectStationScene } from './SelectStationScene';

const RawEditButton = (
  args: Omit<ComponentProps<typeof Ui.IconButton>, 'icon'>,
) => <Ui.IconButton icon={Edit} {...args} />;

const Section = styled.div`
  display: grid;
  grid-template-areas:
    'label_a label_b .'
    'value_a value_b edit';
  grid-template-columns: max-content 1fr max-content;
  grid-gap: 0.2rem 2rem;
  margin-bottom: 2rem;

  &:first-of-type {
    margin-top: 2rem;
  }
`;
const Label = styled.div`
  grid-area: label_a;
  color: ${({ theme }) => theme.color.grey02};
`;
const Value = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  grid-area: value_a;
  font-size: 1rem;
`;
const LabelB = styled(Label)`
  grid-area: label_b;
`;
const ValueB = styled(Value)`
  grid-area: value_b;
`;
const EditButton = styled(RawEditButton)`
  grid-area: edit;
  fill: ${({ theme }) => theme.color.brand};
`;
const StyledArrowForward = styled(ArrowForward)`
  fill: currentcolor;
  width: 24px;
  height: 24px;
`;
const StyledArrowBack = styled(ArrowBack)`
  fill: currentcolor;
  width: 24px;
  height: 24px;
`;
const StyledInfoIcon = styled(Info)`
  fill: ${({ theme }) => theme.color.brand};
  width: 24px;
  height: 24px;
  vertical-align: middle;
  cursor: pointer;
`;

const useCustomerAgeValue = (age: number) => {
  const t = useTranslations('features.stageLegacy.stage.searchForm');
  if (age === DEFAULT_AGE) {
    return t('age.defaultAge');
  }
  return age.toString();
};

type SubSceneKey =
  | 'pickupLocation'
  | 'returnLocation'
  | 'calendar'
  | 'times'
  | 'customerAge';

type Props = SceneProps & {
  onlyLocations?: boolean;
};

const SummaryScene = ({ onlyLocations, ...sceneProps }: Props) => {
  const { state } = useSearchContext();
  if (!state) {
    throw new Error('No search state found');
  }

  const locale = Runtime.useLocale();
  const { dateLong, timeGeneric } = String.dateFormats[locale.language];
  const customerAge = useCustomerAgeValue(state.customerAge);
  const [activeScene, setActiveScene] = React.useState<
    SubSceneKey | undefined
  >();
  const unsetActiveScene = useCallback(
    () => setActiveScene(undefined),
    [setActiveScene],
  );
  const t = useTranslations('features.stageLegacy.stage.searchForm');

  // TODO: handle other sub scenes
  // TODO: tranlsations
  if (activeScene === 'calendar') {
    return (
      <CalendarScene onClose={unsetActiveScene} onConfirm={unsetActiveScene} />
    );
  }

  if (activeScene === 'pickupLocation') {
    return (
      <SelectStationScene
        onClose={unsetActiveScene}
        onConfirm={unsetActiveScene}
        direction="departure"
      />
    );
  }

  if (activeScene === 'returnLocation') {
    return (
      <SelectStationScene
        onClose={unsetActiveScene}
        onConfirm={unsetActiveScene}
        direction="destination"
      />
    );
  }

  return (
    <WizardOverlay
      {...sceneProps}
      title="Your selection"
      buttonText={
        onlyLocations ? 'Confirm pick-up & return location' : 'Start search'
      }
      errorText={
        state.dateError || state.axiosError || state.locationError || ''
      }
    >
      <Section>
        <Label>Pick-up location</Label>
        <Value>
          <StyledArrowForward />
          {state.depLocation?.name ?? ''}
        </Value>
        <EditButton onClick={() => setActiveScene('pickupLocation')} />
      </Section>
      <Section>
        <Label>Return location</Label>
        <Value>
          <StyledArrowBack />
          {state.destLocation?.name ?? state.depLocation?.name ?? ''}
        </Value>
        <EditButton onClick={() => setActiveScene('returnLocation')} />
      </Section>
      {!onlyLocations && (
        <>
          <Section>
            <Label>Travel dates</Label>
            <Value>
              {format(new Date(state.depDate), dateLong)}
              {' - '}
              {format(new Date(state.destDate), dateLong)}
            </Value>
            <EditButton onClick={() => setActiveScene('calendar')} />
          </Section>
          <Section>
            <Label>Pick-up time</Label>
            <Value>{format(new Date(state.depDate), timeGeneric)}</Value>
            <LabelB>Return time</LabelB>
            <ValueB>{format(new Date(state.destDate), timeGeneric)}</ValueB>
            <EditButton onClick={() => setActiveScene('times')} />
          </Section>
          <Section>
            <Label>Driver age</Label>
            <Value>
              <div>{customerAge} years</div>
              <UiLegacy.Tooltip
                content={<UiLegacy.Markup content={t('age.tooltip.text')} />}
                trigger={<StyledInfoIcon />}
              />
            </Value>
            <EditButton onClick={() => setActiveScene('customerAge')} />
          </Section>
        </>
      )}
    </WizardOverlay>
  );
};

export { SummaryScene };
